<script setup lang="ts">
/**
 * The two row arrows beside a ribbon gallery (Write's „Formatvorlagen“): one
 * row of tiles is in view, the arrows page to the previous and next row. The
 * gallery's grid and its scrolling stay the host's; the pager only says which
 * way can move (`can-previous`, `can-next`) and emits `previous` / `next`.
 *
 * The arrows are narrower than a small ribbon command and half its height, so
 * the pair stands beside one row of tiles. A press keeps the focus where it is
 * (the document's caret), like the ribbon's other commands.
 */
import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js'; import ChevronUp from 'lucide-vue-next/dist/esm/icons/chevron-up.js';
import '../office-ribbon.css';

const props = withDefaults(
  defineProps<{
    canPrevious?: boolean;
    canNext?: boolean;
    previousLabel: string;
    nextLabel: string;
  }>(),
  { canPrevious: false, canNext: false }
);

const emit = defineEmits<{ previous: []; next: [] }>();
</script>

<template>
  <div class="nx-ribbon-gallery-pager">
    <button
      type="button"
      class="nx-ribbon-gallery-pager__arrow"
      :title="props.previousLabel"
      :aria-label="props.previousLabel"
      :disabled="!props.canPrevious"
      data-testid="nx-ribbon-gallery-previous"
      @mousedown.prevent
      @click="emit('previous')"
    >
      <ChevronUp class="nxt-icon-sm" aria-hidden="true" />
    </button>
    <button
      type="button"
      class="nx-ribbon-gallery-pager__arrow"
      :title="props.nextLabel"
      :aria-label="props.nextLabel"
      :disabled="!props.canNext"
      data-testid="nx-ribbon-gallery-next"
      @mousedown.prevent
      @click="emit('next')"
    >
      <ChevronDown class="nxt-icon-sm" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.nx-ribbon-gallery-pager {
  display: flex;
  flex: none;
  flex-direction: column;
  justify-content: center;
}
.nx-ribbon-gallery-pager__arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: calc(var(--nxt-size-unit) * 1.125);
  height: calc(var(--nxt-size-unit) * 1.5);
  margin: 0;
  padding: 0;
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: var(--office-ribbon-fg, var(--nxt-fg));
  cursor: pointer;
}
.nx-ribbon-gallery-pager__arrow:hover:not(:disabled) {
  background: var(--office-ribbon-hover-bg, var(--nxt-surface-sunken-bg));
  color: var(--office-ribbon-hover-fg, var(--nxt-brand-bg));
}
.nx-ribbon-gallery-pager__arrow:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}
.nx-ribbon-gallery-pager__arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
